<template>
  <div class="flight-area">
    <div class="main-container">
      <div aria-label="Breadcrumb" class="breadcrumb-container">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ href }">
              <a v-if="flightAreaStore.hasFlightArea" :href="href" @click.prevent="onClickSettings"
                >設定トップ</a
              >
              <span v-else aria-disabled="true">設定トップ</span>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">飛行エリア設定</span>
          </li>
        </ol>
      </div>
      <Form :key="formKey" @submit="handleSave" v-slot="{ meta }">
        <div class="container">
          <h2 class="config-headline">
            <img
              src="../assets/images/icon/icon_location_marker_orange.svg"
              alt=""
              class="icon-config-headline"
            />
            飛行エリア設定
          </h2>
          <div class="tabs-container" id="tab-interface">
            <div class="tab-buttons">
              <button
                v-for="tab in tabs"
                :key="tab.no"
                class="tab-btn"
                :class="{ active: activeTab === tab.no }"
                :data-tab-target="`tab-content-${tab.no}`"
                @click="onClickTab(tab.no)"
                type="button"
              >
                {{ tab.label }}
              </button>
            </div>
            <div class="tab-contents">
              <div class="tab-pane active" id="tab-content-1">
                <div class="flight-area-item">
                  <ul class="flight-area-main">
                    <li v-if="flightArea.flight_area_id === 0">
                      <div class="config-attention">飛行エリアが設定されていません</div>
                    </li>
                    <li class="area-name">
                      <label for="areaName" class="label-l">
                        飛行エリア名 <span class="required-mark">※必須</span>
                      </label>
                      <InputText
                        name="areaName"
                        v-model="flightArea.flight_area_name"
                        rules="required|max_length:20"
                        type="text"
                        :maxlength="20"
                        @blur="onBlur('areaName')"
                      />
                      <ErrorMessageBox v-if="touchedPoints.areaName" name="areaName" />
                    </li>
                    <li class="lon-lat">
                      <fieldset class="fieldset-group">
                        <legend class="fieldset-legend">
                          中心地点 <span class="required-mark">※必須</span>
                        </legend>
                        <ul class="center-point">
                          <li>
                            <label for="" class="label-s">緯度</label>
                            <NumberField
                              name="latitude"
                              v-model="flightArea.center_point_coordinate.latitude"
                              :rules="`required|between:${MIN_LATITUDE},${MAX_LATITUDE},7`"
                              :decimals="7"
                              inputClass="input-text num"
                              @blur="onBlur('latitude')"
                            />
                            <ErrorMessageBox v-if="touchedPoints.latitude" name="latitude" />
                          </li>
                          <li>
                            <label for="" class="label-s">経度</label>
                            <NumberField
                              name="longitude"
                              v-model="flightArea.center_point_coordinate.longitude"
                              :rules="`required|between:${MIN_LONGITUDE},${MAX_LONGITUDE},7`"
                              :decimals="7"
                              inputClass="input-text num"
                              @blur="onBlur('longitude')"
                            />
                            <ErrorMessageBox v-if="touchedPoints.longitude" name="longitude" />
                          </li>
                        </ul>
                      </fieldset>
                      <button
                        class="button-secondary"
                        id="open-map-edit-popup-1"
                        @click="openMapModal"
                        type="button"
                      >
                        <span>
                          <img src="../assets/images/icon/icon_file_map_stack.svg" alt="" />
                        </span>
                        地図から緯度/経度を修正する
                      </button>
                    </li>
                  </ul>
                </div>
                <div class="flight-area-item flight-area-map">
                  <button class="button-secondary" @click="applyToMap" type="button">
                    <span>
                      <img src="../assets/images/icon/icon_move.svg" alt="" />
                    </span>
                    変更を地図に反映する
                  </button>
                  <GsiMap
                    ref="gsiMap"
                    mode="single"
                    :lat="mapLat"
                    :lon="mapLon"
                    :mapType="mapModalStore.mapType"
                    markerContext="flight-area"
                    @click:flight-area="openMapModalFromPin"
                  />
                </div>

                <div class="flight-area-item">
                  <label for="" class="label-l"> コメント </label>
                  <div class="config-memo">
                    <TextareaCounter
                      name="comment"
                      rules="max_length:100"
                      :rows="3"
                      :limit="100"
                      v-model="flightArea.comment"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            id="map-edit-popup"
            class="custom-dialog-overlay js-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
          >
            <div class="custom-dialog-content">
              <h3 id="dialog-title" class="dialog-title">
                地図から緯度/経度を修正する
                <img
                  src="../assets/images/icon/button_close.svg"
                  alt="閉じる"
                  class="close-dialog js-close-dialog-icon"
                />
              </h3>
            </div>
          </div>
        </div>
        <div class="config-action">
          <div class="action-button-group">
            <button
              class="button-secondary alert"
              @click="onClickUndo"
              :disabled="isSaving"
              type="button"
            >
              <span>
                <img src="../assets/images/icon/icon_undo.svg" alt="" />
              </span>
              リセット
            </button>
            <button
              type="button"
              class="button-primary"
              :class="{ 'is-disabled': isSaving || !meta.valid || !isRequiredValidation }"
              :disabled="isSaving || !meta.valid || !isRequiredValidation"
              @click="handleSave"
            >
              設定を保存する
            </button>
          </div>
          <ErrorMessageBox
            v-if="serverErrorMessage"
            :message="serverErrorMessage"
            class="login-action-error-message"
          />
          <div class="delet-all">
            <button
              type="button"
              @click.prevent="handleDelete"
              :disabled="isSaving || flightArea.flight_area_id === 0"
            >
              <img src="../assets/images/icon/icon_delete.svg" alt="" />
              飛行エリアを削除
            </button>
          </div>
        </div>
      </Form>

      <!-- パンくず -->
      <div aria-label="Breadcrumb" class="breadcrumb-container-under">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ href }">
              <a v-if="flightAreaStore.hasFlightArea" :href="href" @click.prevent="onClickSettings"
                >設定トップ</a
              >
              <span v-else aria-disabled="true">設定トップ</span>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">飛行エリア設定</span>
          </li>
        </ol>
      </div>

      <MapModal
        :isOpen="mapModalStore.visible"
        :lat="mapModalStore.lat"
        :lon="mapModalStore.lon"
        :mapType="mapModalStore.mapType"
        mapContext="flight-area"
        @cancel="mapModalStore.cancel()"
        @apply="onApplyFromModal"
      />
      <UndoAlertModal
        :isOpen="confirmModal.visible"
        :title="confirmModal.title"
        :message="confirmModal.message"
        :cancelText="confirmModal.cancelText"
        :confirmText="confirmModal.confirmText"
        @cancel="confirmModal.cancel()"
        @confirm="confirmModal.confirm()"
      />
      <Toast />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, nextTick, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Form } from 'vee-validate'
import { getFieldMessage } from '@/utils/message'
import { useToast } from '@/composables/useToast'
import {
  MAX_FLIGHT_AREA_COUNT,
  MIN_LATITUDE,
  MAX_LATITUDE,
  MIN_LONGITUDE,
  MAX_LONGITUDE,
} from '@/constants'
import NumberField from '@/components/form/NumberField.vue'
import InputText from '@/components/form/InputText.vue'
import TextareaCounter from '@/components/form/TextareaCounter.vue'
import ErrorMessageBox from '@/components/form/ErrorMessageBox.vue'
import Toast from '@/components/common/Toast.vue'
import UndoAlertModal from '@/components/modal/UndoAlertModal.vue'
import MapModal from '@/components/modal/MapModal.vue'
import GsiMap from '@/components/map/GsiMap.vue'
import { useFlightAreasLogic } from '@/composables/FlightAreasLogic'
import { useFlightAreaStore } from '@/stores/flightArea'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useMapModalStore } from '@/stores/mapModal'
import { useConfirmBeforeLeave } from '@/composables/useConfirmBeforeLeave'
import { useConfirmDiscard } from '@/composables/useConfirmDiscard'
import { toValidLat, toValidLon } from '@/utils/common'

const router = useRouter()
const flightAreaStore = useFlightAreaStore()
const gsiMap = ref()
const confirmModal = useConfirmModalStore()
const mapModalStore = useMapModalStore()
const { show } = useToast()
const {
  activeTab,
  flightArea,
  loadFlightAreas,
  isDirty,
  isSaving,
  saveFlightArea,
  removeFlightArea,
} = useFlightAreasLogic()
const touchedPoints = ref<Record<string, boolean>>({})
const onBlur = (name: string) => {
  touchedPoints.value[name] = true
}
const formKey = ref(0)

const tabs = Array.from({ length: MAX_FLIGHT_AREA_COUNT }, (_, i) => ({
  no: i + 1,
  label: `エリア${i + 1}`,
}))
const serverErrorMessage = ref<string>('')

// 地図表示用の緯度経度
const mapLat = ref<number | null>(null)
const mapLon = ref<number | null>(null)

// 戻るボタン
useConfirmBeforeLeave(isDirty)

// 初期表示時
onMounted(async () => {
  await loadFlightAreas()
  mapLat.value = flightArea.value.center_point_coordinate.latitude
  mapLon.value = flightArea.value.center_point_coordinate.longitude
})

watch(
  () => flightArea.value.center_point_coordinate,
  (coord) => {
    nextTick(() => {
      if (coord.latitude != null && coord.longitude != null) {
        mapLat.value = coord.latitude
        mapLon.value = coord.longitude
        gsiMap.value?.reloadMap(mapLat.value, mapLon.value)
      } else {
        mapLat.value = null
        mapLon.value = null
        gsiMap.value?.reloadMap(null, null)
      }
    })
  },
  { immediate: true },
)

watch(
  () => activeTab.value,
  () => {
    resetFormContext()
  },
)

watch(
  () => flightArea.value.flight_area_id,
  (id, prev) => {
    if (prev !== 0 && id === 0) {
      resetFormContext()
    }
  },
)

function resetFormContext() {
  touchedPoints.value = {}
  formKey.value++
}

// 「設定トップ」リンク押下
async function onClickSettings() {
  if (isDirty.value) {
    await confirmDiscardThen(() => {
      isDirty.value = false
      router.push('/settings')
    })
  } else {
    await router.push('/settings')
  }
}

// 「リセット」ボタン押下
async function onClickUndo() {
  await confirmDiscardThen(async () => {
    await loadFlightAreas()
    gsiMap.value?.reloadMap(
      flightArea.value.center_point_coordinate.latitude,
      flightArea.value.center_point_coordinate.longitude,
    )
  })
}

import type { NavigationFailure } from 'vue-router'

type DiscardActionResult = void | Promise<void | NavigationFailure | undefined>

async function confirmDiscardThen(action: () => DiscardActionResult) {
  const { confirmDiscard } = useConfirmDiscard()

  const ok = await confirmDiscard()
  if (ok) {
    await action()
  }
}

// タブ切り替え時
function onClickTab(next: number) {
  if (next === activeTab.value) return
  activeTab.value = next

  nextTick(() => {
    const coord = flightArea.value.center_point_coordinate

    if (coord.latitude != null && coord.longitude != null) {
      mapLat.value = coord.latitude
      mapLon.value = coord.longitude
      gsiMap.value?.reloadMap(mapLat.value, mapLon.value)
    } else {
      mapLat.value = null
      mapLon.value = null
      gsiMap.value?.reloadMap(null, null)
    }
  })
}

const mapModal = useMapModalStore()

async function openMapModal() {
  const result = await mapModal.open({
    lat: flightArea.value.center_point_coordinate.latitude,
    lon: flightArea.value.center_point_coordinate.longitude,
  })
  if (result) {
    mapLat.value = result.lat
    mapLon.value = result.lon
  }
}
// ピンから地図モーダルを開く
async function openMapModalFromPin() {
  const coord = flightArea.value.center_point_coordinate

  const result = await mapModalStore.open({
    lat: coord.latitude,
    lon: coord.longitude,
  })

  if (!result) return

  flightArea.value.center_point_coordinate.latitude = result.lat
  flightArea.value.center_point_coordinate.longitude = result.lon
}

// 「変更を地図に反映する」ボタン押下
const applyToMap = () => {
  const rawLat = Number(flightArea.value.center_point_coordinate.latitude)
  const rawLon = Number(flightArea.value.center_point_coordinate.longitude)

  if (null == toValidLat(rawLat) || null == toValidLon(rawLon)) {
    return
  }

  mapLat.value = rawLat
  mapLon.value = rawLon

  nextTick(() => {
    gsiMap.value?.reloadMap(rawLat, rawLon)
  })
}

function onApplyFromModal({ lat, lon }: { lat: number | string; lon: number | string }) {
  const nextLat = Number(lat)
  const nextLon = Number(lon)

  // フォーム値を更新
  flightArea.value.center_point_coordinate.latitude = nextLat
  flightArea.value.center_point_coordinate.longitude = nextLon

  // 親地図用 state を更新
  mapLat.value = nextLat
  mapLon.value = nextLon

  // 親地図を即時更新
  nextTick(() => {
    gsiMap.value?.reloadMap(nextLat, nextLon)
  })

  // モーダルを閉じる
  mapModalStore.cancel()
}

// 「設定を保存する」ボタン押下
async function handleSave() {
  if (isSaving.value) return
  serverErrorMessage.value = ''
  const response = await saveFlightArea()
  handleServerResponse(response, 'operationncompleted004')
}

// 「飛行エリアを削除」リンク押下
async function handleDelete() {
  if (isSaving.value) return
  serverErrorMessage.value = ''

  // 削除確認モーダルを表示
  const ok = await confirmModal.open({
    title: getFieldMessage('areadiscard001_1'),
    message: getFieldMessage('areadiscard001_2'),
    confirmText: '削除する',
    cancelText: 'キャンセル',
  })
  if (!ok) return

  const response = await removeFlightArea()
  handleServerResponse(response, 'operationdeleted005')
}

function handleServerResponse(response: any, messageId: string) {
  const messageIds = response && 'message_ids' in response ? (response.message_ids ?? []) : []
  if (messageIds && messageIds.length > 0) {
    serverErrorMessage.value = getFieldMessage(messageIds)
  } else {
    loadFlightAreas()
    show(getFieldMessage(messageId))
  }
}
// エリア未登録かつ全項目未入力の場合、バリデーションを無効化
const isRequiredValidation = computed(
  () =>
    !(
      flightArea.value.flight_area_id === 0 &&
      flightArea.value.center_point_coordinate.latitude === null &&
      flightArea.value.center_point_coordinate.longitude === null &&
      (flightArea.value.flight_area_name === '' || flightArea.value.flight_area_name === null)
    ),
)
</script>
